import { z } from 'zod';
/**
 * A surface that a conversational thread is anchored to.
 *
 * - `entity`: thread is "about" a specific dossier. The chat sees the
 *   entity name + a slim grounding context (top facts, current synthesis
 *   highlights) folded into the system prompt.
 * - `graph`: thread is "about" the current graph view (mode, focus
 *   entity, hop count). Used so a chat opened on /graph?preset=parsi-bridge
 *   knows it's discussing that subgraph.
 * - `free`: command-center / no anchor. Equivalent to `subject: undefined`
 *   but explicit so the threads sidebar can render a label.
 */
export const ThreadSubject = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('entity'),
        name: z.string(),
    }),
    z.object({
        kind: z.literal('graph'),
        mode: z.enum(['overview', 'focus', 'path']).optional(),
        focusEntity: z.string().optional(),
        preset: z.string().optional(),
        hops: z.number().int().min(1).max(4).optional(),
    }),
    z.object({
        kind: z.literal('free'),
    }),
]);
/**
 * Transient view state the analyst has built up on the surface they're
 * chatting from — filters, hides, pins, selection. Sent on every
 * `/api/ask` so the assistant reasons about the same subgraph the
 * analyst is actually looking at; explicitly NOT persisted to the
 * thread doc because re-opening a thread later shouldn't re-apply old
 * filters.
 *
 * Caps mirror the watchlist convention: per-name length capped, list
 * length capped — keeps prompt size bounded and resists prompt-
 * injection via crafted entity names. The server applies the same
 * `sanitizeWatchlistName` pass before stitching names into the prompt.
 */
export const SurfaceState = z.object({
    /** Visible entity-type set. Omit / empty = no filter (show all). */
    visibleLabels: z.array(z.string().min(1).max(40)).max(8).optional(),
    /** Visible relationship-type set. Omit / empty = show all. */
    visibleRelTypes: z.array(z.string().min(1).max(80)).max(40).optional(),
    /** Names the analyst has explicitly hidden (right-click → Hide). */
    hiddenEntities: z.array(z.string().min(1).max(80)).max(20).optional(),
    /** Pinned entity names (locked positions on the canvas). */
    pinnedEntities: z.array(z.string().min(1).max(80)).max(20).optional(),
    /** Current multi-select set (shift-drag marquee or shift-click). */
    selectedEntities: z.array(z.string().min(1).max(80)).max(20).optional(),
});
/**
 * A side-effect the assistant requests on the user's view (Stage 2 of
 * multi-turn conversations — frontend spec §5.5 generative actions).
 *
 * The model emits these inside `[ACTION]{...}[/ACTION]` blocks in its
 * answer; the server parses them off the streamed text and forwards them
 * over the SSE wire as typed `action` events. The client dispatcher then
 * performs the side-effect (watchlist write, navigation, etc.) and
 * surfaces a confirmation chip in the chat.
 *
 * Trust boundary: actions invoke EXISTING typed endpoints (watchlist,
 * router navigation, etc.) — they don't widen the analyst's permissions.
 * The model can ask, but the dispatcher only fires safe, idempotent
 * verbs. New verbs require an entry here AND a dispatcher case AND a
 * conscious code review.
 */
export const ChatAction = z.discriminatedUnion('verb', [
    // Pin an entity to the analyst's watchlist. Idempotent server-side.
    z.object({
        verb: z.literal('pinEntity'),
        entityName: z.string(),
        reason: z.string().optional(),
    }),
    // Remove an entity from the watchlist.
    z.object({
        verb: z.literal('unpinEntity'),
        entityName: z.string(),
    }),
    // Deep-link the graph page to a focus subgraph.
    z.object({
        verb: z.literal('focusSubgraph'),
        entityName: z.string(),
        hops: z.number().int().min(1).max(4).optional(),
    }),
    // Open an entity dossier (router push). The most common navigation verb.
    z.object({
        verb: z.literal('openDossier'),
        entityName: z.string(),
    }),
    // Open the side-by-side compare view for two entities.
    z.object({
        verb: z.literal('compareEntities'),
        a: z.string(),
        b: z.string(),
    }),
]);
/**
 * The bracket markers Claude emits around a chat-action JSON block.
 * Mirrors the existing `[RENDER]...[/RENDER]` convention so the parser
 * surface stays small and predictable.
 */
export const CHAT_ACTION_OPEN = '[ACTION]';
export const CHAT_ACTION_CLOSE = '[/ACTION]';
